<template>
  <div>
    <assessment-helper></assessment-helper>

    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" class="a-letters-kpm">
        <figure class="keyboard-k2 letter-wrapper focusable" tabindex="0">
          <div :class="currentKey" :style="{ backgroundColor: currentKeyColor }"></div>
        </figure>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';
import ActivityStage from 'components/activity/ActivityStage.vue';
import AssessmentHelper from 'components/utils/AssessmentHelper.vue';
import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import { convertSymbolToName } from 'utils/key-util';

const lettersQueues = {
  nrow: '`1234567890-=',
  qrow: 'qwertyuiop[]',
  arow: "asdfghjkl;'\\",
  zrow: 'zxcvbnm,./'
};

const targetColors = {
  nrow: '#FAD4A4',
  qrow: '#FFF6CB',
  arow: '#DFEBBE',
  zrow: '#D2E6EE',
  rowSpace: '#d4d1e3'
};

export default {
  components: {
    ActivityStage,
    AssessmentHelper
  },

  mixins: [ActivityManager, AssetLoader, ErrorLogger, TypingActivityHelper],

  data() {
    return {
      activityName: 'ALettersKPM',
      currentKey: undefined,
      needsSwitch: false
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    currentKeyColor() {
      if (this.currentStep === ' ') return targetColors.rowSpace;
      if (this.currentKey === 'keyabc switchKey' || this.currentKey === 'key123 switchKey')
        return targetColors.rowSpace;

      let keyColor = '';

      Object.entries(lettersQueues).some(([key, value]) => {
        if ([...value].includes(this.currentStep)) {
          keyColor = key;
          return true;
        }
        return false;
      });

      return targetColors[keyColor];
    }
  },

  methods: {
    async loadActivity() {
      this.createRounds({
        stepIndicator: 'keystrokes'
      });
    },

    getCurrentKey() {
      let currentKey = '';
      const { expectedKey: currentStep } = this;

      const key =
        `key${convertSymbolToName(currentStep)}` || `special-${convertSymbolToName(currentStep)}`;

      const keyElement = document.getElementsByClassName(key)[0];

      if (keyElement) {
        this.needsSwitch = false;
        currentKey = keyElement.classList.value;
      }

      if (key && !keyElement) {
        this.needsSwitch = true;
        currentKey = `${/\d/.test(key) ? 'key123' : 'keyabc'} switchKey`;
      }

      return currentKey;
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard || {
        keyboardType: undefined,
        roofs: undefined
      };

      return {
        keyboardType: keyboardType || 'KeyboardK2',
        roofs
      };
    },

    startActivity() {
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        this.currentKey = this.getCurrentKey();

        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
        TweenMax.to('.letter-wrapper', 0.75, {
          opacity: 1,
          onComplete: this.enableKeyboard
        });
      });
    },

    stepCallback() {
      return this.currentStep;
    },

    handleCorrectKey(attempt) {
      this.disableKeyboard();
      this.sndType();

      this.captureKeyboardAttemptSegment(attempt, true);

      TweenMax.to('.letter-wrapper', 0.2, {
        opacity: 0,
        onComplete: this.finishStep
      });
    },

    finishStep() {
      if (this.isLastStepInRound) {
        this.sndBell();
        this.hideKeyboard();
        TweenMax.delayedCall(0.5, this.handleRoundEnd);
      } else {
        this.completeStep();
        this.setCurrentStep({ stepCallback: this.stepCallback });

        this.currentKey = this.getCurrentKey();

        TweenMax.to('.letter-wrapper', 0.2, {
          opacity: 1,
          onComplete: this.enableKeyboard
        });
      }
    },

    handleRoundEnd() {
      this.completeActivity();
    }
  },
  watch: {
    currentStep(newVal) {
      if (newVal && typeof newVal === 'string') {
        let instructions = `Type the ${newVal} key.`;

        this.setTotalKeyboardingSegment(newVal.toUpperCase());
        this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
      }
    },
    showSpecialKeys() {
      this.needsSwitch = false;
      this.$nextTick(() => {
        this.currentKey = this.getCurrentKey();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';
@import '../assets/sass/keyboards/partials/keyboardk2art.scss';

.a-letters-kpm {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 410px;
  .letter-wrapper {
    height: 134px;
    opacity: 0;
    width: 134px;

    div {
      background-size: contain;
      height: 100%;
    }
  }
}

.chrome-os {
  .a-letters-kpm {
    height: 310px;
  }
}

.tablet {
  .a-letters-kpm {
    height: 100%;
  }
}
</style>
